import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as ApplicationsService from '../services/ApplicationsService';
import { Application } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const applicationsKeys = {
    all: ['applications'] as const,
    lists: () => [...applicationsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...applicationsKeys.lists(), { filters }] as const,
    details: () => [...applicationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...applicationsKeys.details(), id] as const,
};

// Hooks
export const useApplicationsQuery = (filters: Record<string, string> = {}) => {
    return useQuery({
        queryKey: applicationsKeys.list(filters),
        queryFn: () => ApplicationsService.fetchRecords(filters),
    });
};

export const useApplicationQuery = (id: string) => {
    return useQuery({
        queryKey: applicationsKeys.detail(id),
        queryFn: () => ApplicationsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationsService.add,
        onSuccess: () => {
            notifySuccess('Application created');
            queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: Application }) =>
            ApplicationsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Application updated');
            queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: applicationsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeleteApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ApplicationsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Application deleted');
            queryClient.invalidateQueries({ queryKey: applicationsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {applicationStatus: 'ACTIVE'};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useApplicationsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
